import { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { api } from './lib/api';
import { AppShell } from './components/layout/AppShell';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ReviewQueuePage } from './pages/ReviewQueuePage';
import { DiffViewPage } from './pages/DiffViewPage';
import { PublishQueuePage } from './pages/PublishQueuePage';
import { AttestationsPage } from './pages/AttestationsPage';
import { ArchivePage } from './pages/ArchivePage';
import { AdminPage } from './pages/AdminPage';
import { ComplianceProjectsPage } from './pages/ComplianceProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { ConnectorsPage } from './pages/admin/ConnectorsPage';
import { DepartmentsPage } from './pages/admin/DepartmentsPage';
import { ReviewersPage } from './pages/admin/ReviewersPage';
import { AuditTrailPage } from './pages/AuditTrailPage';
import { EscalationsPage } from './pages/EscalationsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { EscalationRulesPage } from './pages/admin/EscalationRulesPage';
import { IngestPage } from './pages/admin/IngestPage';
import { PolicyReviewListPage } from './pages/PolicyReviewListPage';
import { PolicyReviewPage } from './pages/PolicyReviewPage';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { SignupPage } from './pages/SignupPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { InvitePage } from './pages/InvitePage';
import DocumentLibraryPage from './pages/DocumentLibraryPage';
import CascadeViewPage from './pages/CascadeViewPage';
import PlatformDashboard from './pages/admin/PlatformDashboard';
import MyTrainingPage from './pages/MyTrainingPage';
import TrainingDashboardPage from './pages/TrainingDashboardPage';
import TrainingPackageReviewPage from './pages/TrainingPackageReviewPage';
import TrainingEvidencePage from './pages/TrainingEvidencePage';
import OnboardingChatPage from './pages/OnboardingChatPage';
import OrganizationPage from './pages/admin/OrganizationPage';
import SuperAdminPage from './pages/admin/SuperAdminPage';
import WebinarAdminPage from './pages/admin/WebinarAdminPage';
import ComplianceScorePage from './pages/ComplianceScorePage';

function ProtectedLayout() {
  const { user, isLoading, setUser, clearUser } = useAuthStore();

  useEffect(() => {
    if (!user) {
      console.log('[Auth] Attempting session restore...');
      api.auth.me()
        .then((u) => {
          console.log('[Auth] Session restored:', u.email);
          setUser(u);
        })
        .catch((err: unknown) => {
          console.log('[Auth] Session restore failed:', err instanceof Error ? err.message : err);
          clearUser();
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <AppShell />;
}

function AdminGuard() {
  const hasPermission = useAuthStore((s) => s.hasPermission);

  if (!hasPermission('manage_tenant')) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/platform-admin" element={<PlatformDashboard />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route element={<ProtectedLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="review" element={<ReviewQueuePage />} />
        <Route path="review/:sessionId" element={<DiffViewPage />} />
        <Route path="publish" element={<PublishQueuePage />} />
        <Route path="attestations" element={<AttestationsPage />} />
        <Route path="projects" element={<ErrorBoundary fallbackTitle="Failed to load projects"><ComplianceProjectsPage /></ErrorBoundary>} />
        <Route path="projects/:projectId" element={<ErrorBoundary fallbackTitle="Failed to load project"><ProjectDetailPage /></ErrorBoundary>} />
        <Route path="document-library" element={<ErrorBoundary fallbackTitle="Failed to load document library"><DocumentLibraryPage /></ErrorBoundary>} />
        <Route path="cascades" element={<ErrorBoundary fallbackTitle="Failed to load cascades"><CascadeViewPage /></ErrorBoundary>} />
        <Route path="training" element={<ErrorBoundary fallbackTitle="Failed to load training"><MyTrainingPage /></ErrorBoundary>} />
        <Route path="training/dashboard" element={<ErrorBoundary fallbackTitle="Failed to load training dashboard"><TrainingDashboardPage /></ErrorBoundary>} />
        <Route path="training/evidence" element={<ErrorBoundary fallbackTitle="Failed to load training evidence"><TrainingEvidencePage /></ErrorBoundary>} />
        <Route path="training/packages/:packageId" element={<ErrorBoundary fallbackTitle="Failed to load training package"><TrainingPackageReviewPage /></ErrorBoundary>} />
        <Route path="policy-review" element={<ErrorBoundary fallbackTitle="Failed to load policy reviews"><PolicyReviewListPage /></ErrorBoundary>} />
        <Route path="policy-review/:sessionId" element={<ErrorBoundary fallbackTitle="Failed to load policy review"><PolicyReviewPage /></ErrorBoundary>} />
        <Route path="escalations" element={<ErrorBoundary fallbackTitle="Failed to load escalations"><EscalationsPage /></ErrorBoundary>} />
        <Route path="notifications" element={<ErrorBoundary fallbackTitle="Failed to load notifications"><NotificationsPage /></ErrorBoundary>} />
        <Route path="audit-trail" element={<ErrorBoundary fallbackTitle="Failed to load audit trail"><AuditTrailPage /></ErrorBoundary>} />
        <Route path="archive" element={<ArchivePage />} />
        <Route element={<AdminGuard />}>
          <Route path="admin" element={<AdminPage />} />
          <Route path="admin/connectors" element={<ConnectorsPage />} />
          <Route path="admin/departments" element={<DepartmentsPage />} />
          <Route path="admin/reviewers" element={<ReviewersPage />} />
          <Route path="admin/escalation-rules" element={<EscalationRulesPage />} />
          <Route path="admin/ingest" element={<IngestPage />} />
          <Route path="admin/organization" element={<OrganizationPage />} />
          <Route path="admin/super" element={<SuperAdminPage />} />
          <Route path="admin/webinars" element={<WebinarAdminPage />} />
        </Route>
        <Route path="onboarding/chat" element={<OnboardingChatPage />} />
        <Route path="dashboard/score" element={<ComplianceScorePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
